import React, { useState } from 'react';
import styled from 'styled-components';
import { useI18n } from '../i18n';
import { en, km } from './VideoGallery.data';

/**
 * VideoGallery component displays a list of videos with titles
 * that can be clicked to open in a modal dialog
 * 
 * @param {Object[]} videos - Array of video objects
 * @param {string} videos[].title - Title of the video
 * @param {string} videos[].youtubeId - YouTube video ID
 * @param {string} [videos[].description] - Optional description of the video
 */
const VideoGallery = ({ videos }) => {
  const { locale } = useI18n();
  const t = locale === 'km' ? km : en;
  const [activeVideo, setActiveVideo] = useState(null);

  const openVideo = (video) => {
    setActiveVideo(video);
  };

  const closeVideo = () => {
    setActiveVideo(null);
  };

  const getThumbnailUrl = (youtubeId) => {
    return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  };

  return (
    <GalleryContainer>
      <VideoGrid>
        {videos.map((video, index) => (
          <VideoCard key={index} onClick={() => openVideo(video)} role="button">
            <ThumbnailWrapper>
              <Thumbnail 
                src={getThumbnailUrl(video.youtubeId)} 
                alt={video.title}
              />
              <PlayOverlay>
                <PlayButton aria-label={t.playVideo}>
                  <PlayIcon>▶</PlayIcon>
                </PlayButton>
              </PlayOverlay>
            </ThumbnailWrapper>
            <CardContent>
              <VideoTitle>{video.title}</VideoTitle>
              {video.description && <VideoDescription>{video.description}</VideoDescription>}
            </CardContent>
          </VideoCard>
        ))}
      </VideoGrid>

      {activeVideo && (
        <VideoDialog>
          <DialogOverlay onClick={closeVideo} />
          <DialogContent>
            <CloseButton onClick={closeVideo} aria-label={t.closeVideo}>×</CloseButton>
            <VideoTitle className="dialog-title">{activeVideo.title}</VideoTitle>
            <VideoContainer>
              <iframe 
                width="100%" 
                height="100%" 
                src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1`}
                title={activeVideo.title}
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </VideoContainer>
            {activeVideo.description && (
              <VideoDescription className="dialog-description">
                {activeVideo.description}
              </VideoDescription>
            )}
          </DialogContent>
        </VideoDialog>
      )}
    </GalleryContainer>
  );
};

const GalleryContainer = styled.div`
  width: 100%;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const VideoCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  &:hover div[class*="PlayOverlay"] {
    opacity: 1;
  }
  
  &:hover div[class*="PlayButton"] {
    transform: scale(1.1);
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${VideoCard}:hover & {
    transform: scale(1.05);
  }
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.3s ease;
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(255, 0, 0, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, background 0.3s ease;
  
  &:hover {
    background: rgba(255, 0, 0, 1);
  }
`;

const PlayIcon = styled.span`
  font-size: 1.5rem;
  color: white;
  margin-left: 4px;
`;

const CardContent = styled.div`
  padding: 1rem 1.25rem;
  text-align: left;
`;

const VideoTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--text-dark, #333);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &.dialog-title {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: white;
    -webkit-line-clamp: unset;
  }
`;

const VideoDescription = styled.p`
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &.dialog-description {
    margin-top: 1rem;
    font-size: 1rem;
    color: #e0e0e0;
    -webkit-line-clamp: unset;
  }
`;

const VideoDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const DialogOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const DialogContent = styled.div`
  background-color: #121212;
  padding: 2rem;
  border-radius: 10px;
  width: 95%;
  height: 90%;
  max-width: 95%;
  max-height: 90%;
  overflow: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  font-size: 1.8rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const VideoContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 1rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

export default VideoGallery; 